import { useState } from "react";
import type { Cohort, Invite2, Person2 } from "@/app/peopleQueries";
import { Button } from "@/ui/Button";
import { Eyebrow, Dot, INPUT_CLS, SELECT_CLS, TONE_TEXT } from "./shared";
import { NO_COHORT_META, ROLE_FILTERS, cohortMeta, inviteState, people, roleCounts, type RoleFilter, type Scope } from "./model";

/** Карточки когорт = переключатель режима (правило 6 CLAUDE.md): все · без когорты · когорта. */
export function CohortCards({ cohorts, noCohort, scope, onScope }: { cohorts: Cohort[]; noCohort: number; scope: Scope; onScope: (s: Scope) => void }) {
  const total = cohorts.reduce((s, c) => s + c.students, 0) + noCohort;
  const items: { id: Scope; name: string; n: number; meta: string; dot: string }[] = [
    { id: "all", name: "Все студенты", n: total, meta: "включая тех, кто вне когорт", dot: "info" },
    { id: "nocohort", name: "Без когорты", n: noCohort, meta: NO_COHORT_META, dot: "bd2" },
    ...cohorts.map((c) => {
      const m = cohortMeta(c);
      return { id: c.id, name: c.name, n: c.students, meta: m.label, dot: m.tone };
    }),
  ];
  return (
    <div className="flex flex-col gap-1.5" role="group" aria-label="когорты">
      {items.map((it) => {
        const on = it.id === scope;
        return (
          <button
            key={it.id}
            type="button"
            aria-pressed={on}
            data-testid={`cohort-card-${it.id}`}
            data-count={it.n}
            onClick={() => onScope(it.id)}
            className={`flex flex-col gap-0.5 rounded-md border px-[11px] py-2.5 text-left ${on ? "border-acc bg-card text-ink" : "border-bd bg-transparent text-ink2 hover:border-bd2"}`}
          >
            <span className="flex items-center gap-2 text-[12.5px]">
              <Dot tone={it.dot} />
              <span className="flex-1 truncate">{it.name}</span>
              <span className="font-mono text-[10.5px] text-mut">{it.n === 0 && it.dot === "warn" ? "набор" : people(it.n)}</span>
            </span>
            <span className="pl-3.5 font-mono text-[10.5px] text-mut">{it.meta}</span>
          </button>
        );
      })}
    </div>
  );
}

const STATUS_OPTS: readonly { id: Cohort["status"]; label: string }[] = [
  { id: "recruiting", label: "набор" },
  { id: "active", label: "идёт" },
  { id: "finished", label: "завершена" },
];

/** «+ когорта»: имя, статус, дата старта — встроенная форма. */
export function NewCohortForm({ onCreate, pending, error }: { onCreate: (b: { name: string; status: Cohort["status"]; starts_on?: string }) => Promise<unknown>; pending: boolean; error?: string }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [status, setStatus] = useState<Cohort["status"]>("recruiting");
  const [starts, setStarts] = useState("");
  if (!open)
    return (
      <Button size="sm" className="self-start" onClick={() => setOpen(true)} data-testid="new-cohort">
        + когорта
      </Button>
    );
  const empty = name.trim() === "";
  return (
    <form
      className="flex flex-col gap-2 rounded-md border border-bd2 bg-bg2 p-2.5"
      data-testid="new-cohort-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (empty) return;
        void onCreate({ name: name.trim(), status, starts_on: starts || undefined }).then(() => {
          setName("");
          setStarts("");
          setOpen(false);
        });
      }}
    >
      <input aria-label="название когорты" placeholder="ML-зима-2027" value={name} onChange={(e) => setName(e.target.value)} className={INPUT_CLS} autoFocus />
      <div className="flex gap-2">
        <select aria-label="статус когорты" value={status} onChange={(e) => setStatus(e.target.value as Cohort["status"])} className={`${SELECT_CLS} flex-1`}>
          {STATUS_OPTS.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
        <input type="date" aria-label="дата старта" value={starts} onChange={(e) => setStarts(e.target.value)} className={`${SELECT_CLS} flex-1`} />
      </div>
      {error && (
        <span className="font-mono text-[11px] text-err" role="alert">
          {error}
        </span>
      )}
      <div className="flex items-center gap-2">
        <Button size="sm" variant="primary" type="submit" disabled={empty || pending} reason={empty ? "введите название" : pending ? "создаём…" : undefined}>
          создать
        </Button>
        <span className="flex-1" />
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          отмена
        </Button>
      </div>
    </form>
  );
}

/** Фильтр ролей: счётчики — из строк текущего режима, поиск их не меняет. */
export function RoleFilters({ rows, value, onChange }: { rows: Person2[]; value: RoleFilter; onChange: (r: RoleFilter) => void }) {
  const counts = roleCounts(rows);
  return (
    <div className="flex flex-col gap-0.5" role="group" aria-label="роли">
      {ROLE_FILTERS.map((f) => {
        const on = f.id === value;
        return (
          <button
            key={f.id}
            type="button"
            aria-pressed={on}
            data-testid={`role-${f.id}`}
            data-count={counts[f.id]}
            onClick={() => onChange(f.id)}
            className={`flex items-center justify-between rounded-sm px-2.5 py-1.5 font-mono text-[12px] ${on ? "bg-accsoft text-acc" : "bg-transparent text-ink2 hover:text-ink"}`}
          >
            <span>{f.label}</span>
            <span className={`text-[11px] ${on ? "text-acc" : "text-mut"}`}>{counts[f.id]}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Список приглашений: «ожидает N дней» (≤ 2 — жёлтый, ≥ 5 — красный), «принято». */
export function InvitesList({ invites }: { invites: Invite2[] }) {
  if (invites.length === 0) return <p className="m-0 font-mono text-[11.5px] text-mut">приглашений пока нет</p>;
  const order = { ожидает: 0, истекло: 1, принято: 2 };
  const sorted = [...invites].sort((a, b) => order[a.state] - order[b.state] || b.days - a.days);
  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0" data-testid="invites">
      {sorted.map((inv) => {
        const st = inviteState(inv);
        return (
          <li key={inv.id} className="flex flex-col gap-0.5" data-testid="invite-row" data-state={inv.state}>
            <span className="truncate font-mono text-[11.5px] text-ink2" title={inv.email}>
              {inv.email}
            </span>
            <span className={`flex items-center gap-1.5 font-mono text-[10.5px] ${TONE_TEXT[st.tone]}`}>
              <Dot tone={st.tone} />
              {st.label}
              {inv.cohort && <span className="text-mut">· {inv.cohort}</span>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export { Eyebrow };
